import { DOCUMENT } from '@angular/common';
import { AfterViewInit, Component, ElementRef, HostListener, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import {
  hasMiniMenuModifiers,
  LOADING_MINI_MENU_PUBLIC_CONTENT,
  MiniMenuPublicCategory,
  MiniMenuPublicContent,
  MiniMenuPublicImageSet,
  MiniMenuPublicLanguage,
  MiniMenuPublicModifierGroup,
  MiniMenuPublicModifierOption,
  MiniMenuPublicProduct,
  MiniMenuPublicPromotion
} from 'src/app/models/mini-menu-public.model';
import { MiniMenuService } from 'src/app/services/mini-menu.service';
import { miniMenuDefaultSlug } from 'src/app/mini-menu-host';

type MiniMenuView = 'teaser' | 'menu' | 'promos';

const MINI_MENU_COPY: Record<MiniMenuPublicLanguage, Record<string, string>> = {
  en: {
    menu: 'Menu',
    whatsOn: "What's On",
    back: 'Back',
    more: 'More',
    less: 'Less',
    modifiers: 'Options',
    noItems: 'No menu items are available yet.',
    unavailable: 'This business menu is not currently available.',
    error: 'The menu could not be loaded. Please try again later.',
    loading: 'Loading menu...'
  },
  'zh-CN': {
    menu: '菜单',
    whatsOn: '最新消息',
    back: '返回',
    more: '展开',
    less: '收起',
    modifiers: '选项',
    noItems: '目前没有可用的菜单项目。',
    unavailable: '此商家的菜单目前不可用。',
    error: '无法载入菜单，请稍后再试。',
    loading: '正在载入菜单...'
  }
};

/**
 * The HUNGERY marks shown when a business has not supplied its own. Lockup follows position:
 * the vertical one in the middle of the landing screen, the horizontal one along the top bar.
 * Master art lives in "UI Concepts" (flat #6D4F80 on transparent), so these double as alpha
 * masks and the brand purple is applied in CSS.
 */
const MINI_MENU_FALLBACK_LOGO_VERTICAL = 'assets/images/brand/hungery-vertical.png';
const MINI_MENU_FALLBACK_LOGO_HORIZONTAL = 'assets/images/brand/hungery-horizontal.png';

/**
 * The MiniMenu typography (Hanken Grotesk body, IBM Plex Mono labels) plus every theme
 * font an operator can choose in the MiniMenu settings.
 *
 * This is loaded as a <link> at runtime rather than an `@import` in the component's SCSS:
 * Angular strips `@import url(...)` from component stylesheets, so the import loaded
 * nothing and the whole design silently rendered in system fonts. Injecting it here also
 * keeps eight families off every other HUNGERY page.
 */
const MINI_MENU_FONT_LINK_ID = 'mini-menu-fonts';
const MINI_MENU_FONT_HREF = 'https://fonts.googleapis.com/css2'
  + '?family=Archivo:wght@300;400;500'
  + '&family=Bebas+Neue'
  + '&family=Caveat:wght@400;500;600'
  + '&family=Fraunces:opsz,wght@9..144,300;9..144,400;9..144,500'
  + '&family=Hanken+Grotesk:wght@300;400;500;700;800'
  + '&family=IBM+Plex+Mono:wght@400;500;600;700'
  + '&family=Libre+Baskerville:wght@400;700'
  + '&family=Playfair+Display:wght@400;500'
  + '&display=swap';

@Component({
  selector: 'app-mini-menu-site',
  templateUrl: './mini-menu-site.component.html',
  // Split across several sheets in cascade order: Angular measures the
  // `anyComponentStyle` budget per stylesheet, and this component carries a whole
  // site's worth of CSS — one combined sheet exceeded the production limit.
  styleUrls: [
    './mini-menu-site.component.scss',
    './mini-menu-site.promos.scss',
    './mini-menu-site.tablet.scss',
    './mini-menu-site.features.scss'
  ]
})
export class MiniMenuSiteComponent implements OnInit, AfterViewInit, OnDestroy {
  content: MiniMenuPublicContent = { ...LOADING_MINI_MENU_PUBLIC_CONTENT };
  activeView: MiniMenuView = 'teaser';
  activeCategoryId = '';
  selectedPromotion: MiniMenuPublicPromotion | null = null;
  selectedLanguage: MiniMenuPublicLanguage = 'en';
  languageMenuOpen = false;

  // Tablet widens the phone shell into a persistent dual pane: brand on the left,
  // product list on the right. Portrait vs landscape changes the (always portrait)
  // detail image pane shape, so each picks a different crop — see pickCrop().
  isTabletLayout = false;
  isTabletPortrait = false;

  // What's On is a transient slide-up sheet over the teaser: it rises with staggered
  // rows and collapses on its own after 8s of no interaction.
  whatsOnOpen = false;
  whatsOnClosing = false;
  menuScrolling = false;

  // Teaser subline: clamped to three lines with a More control, which re-collapses on
  // its own after a idle window so the landing composition restores itself.
  sublineExpanded = false;
  sublineOverflows = false;

  // Product descriptions clamp to three lines. At most one is expanded at a time and it
  // stays open until something else is interacted with — no timer.
  expandedProductId = '';

  // Memoized so *ngFor is not handed a new array on every change-detection read.
  menuCategories: MiniMenuPublicCategory[] = [];
  livePromotions: MiniMenuPublicPromotion[] = [];

  private readonly subscriptions = new Subscription();
  private readonly languageStorageKey = 'hungery-mini-menu-language';
  private readonly whatsOnIdleMs = 8000;
  private readonly whatsOnExitMs = 500;
  private readonly sublineIdleMs = 5000;
  // How long the scroll spy stands down after a category tap, so the smooth scroll can
  // land without every category in between flashing active on the way past.
  private readonly categoryJumpMs = 800;
  // A heading counts as "reached the top" once it is within this many px of the top of
  // the list area (the topbar sits outside the scroller, so this stays small).
  private readonly categorySpyOffset = 8;
  private categoryJumpInFlight = false;
  private categoryJumpTimer?: number;
  private whatsOnTimer?: number;
  private whatsOnExitTimer?: number;
  private sublineTimer?: number;
  private menuScrollTimer?: number;
  private layoutRaf?: number;
  private resizeObserver?: ResizeObserver;

  // Left-edge swipe-back (mobile): start within ~28px of the left edge and drag right
  // past ~70px with limited vertical drift to step back toward the landing page.
  private edgeSwipe = { active: false, startX: 0, startY: 0 };
  private readonly edgeSwipeZone = 28;
  private readonly edgeSwipeMinX = 70;
  private readonly edgeSwipeMaxY = 55;

  @ViewChild('appShell') private appShell?: ElementRef<HTMLElement>;
  @ViewChild('sublineText') private sublineText?: ElementRef<HTMLElement>;

  constructor(
    private route: ActivatedRoute,
    private miniMenuService: MiniMenuService,
    @Inject(DOCUMENT) private document: Document
  ) {}

  get themeFontFamily(): string {
    const fonts: Record<string, string> = {
      hanken: "'Hanken Grotesk', system-ui, sans-serif",
      caveat: "'Caveat', cursive",
      playfair: "'Playfair Display', serif",
      bebas: "'Bebas Neue', sans-serif",
      fraunces: "'Fraunces', serif",
      archivo: "'Archivo', sans-serif",
      libre: "'Libre Baskerville', serif"
    };
    return fonts[this.content.theme.font] || fonts['hanken'];
  }

  get logoMaskStyle(): string {
    return this.logoMask(this.content.theme.bannerLogoUrl, MINI_MENU_FALLBACK_LOGO_HORIZONTAL);
  }

  get landingLogoMaskStyle(): string {
    return this.logoMask(this.content.theme.landingLogoUrl, MINI_MENU_FALLBACK_LOGO_VERTICAL);
  }

  // Ordering is off: no add control, no quantity stepper, no cart ribbon. The service
  // resolves this from Evolution AND the MINI_MENU_ORDERING_ENABLED kill switch.
  get orderingAvailable(): boolean {
    return this.content.orderingAvailable;
  }

  get showMenuAction(): boolean {
    return this.content.loadState === 'ready';
  }

  get menuStateMessage(): string {
    if (this.content.loadState === 'loading') {
      return this.ui('loading');
    }
    if (this.content.loadState === 'unavailable') {
      return this.ui('unavailable');
    }
    if (this.content.loadState === 'error') {
      return this.ui('error');
    }
    return this.ui('noItems');
  }

  get languageOptions() {
    return this.content.languages;
  }

  get selectedLanguageLabel(): string {
    return this.languageOptions.find((language) =>
      language.code === this.selectedLanguage)?.label || 'English';
  }

  get selectedLanguageShortLabel(): string {
    return this.languageShortLabel(this.selectedLanguage);
  }

  ngOnInit(): void {
    this.ensureFonts();
    this.selectedLanguage = this.readStoredLanguage();
    this.subscriptions.add(
      this.route.paramMap.subscribe((params) => {
        const slug = String(params.get('slug') || miniMenuDefaultSlug() || '').trim();
        this.load(slug);
      })
    );
  }

  ngAfterViewInit(): void {
    this.scheduleLayoutUpdate();
    // Recompute whenever the shell's width actually changes (CSS widening it at tablet
    // breakpoints, rotation, fonts settling) so tablet mode is not missed by an early
    // one-shot measurement. The teaser is observed too because on tablet it becomes a
    // narrower left pane WITHOUT the shell width changing.
    const shell = this.appShell?.nativeElement;
    if (shell && typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => this.scheduleLayoutUpdate());
      this.resizeObserver.observe(shell);
      const teaser = shell.querySelector('#teaser');
      if (teaser) {
        this.resizeObserver.observe(teaser);
      }
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.resizeObserver?.disconnect();
    window.clearTimeout(this.whatsOnTimer);
    window.clearTimeout(this.whatsOnExitTimer);
    window.clearTimeout(this.menuScrollTimer);
    window.clearTimeout(this.sublineTimer);
    window.clearTimeout(this.categoryJumpTimer);
    if (this.layoutRaf) {
      window.cancelAnimationFrame(this.layoutRaf);
    }
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.scheduleLayoutUpdate();
  }

  // Any interaction outside an expanded description collapses it — that is the whole
  // "other interactions contract it" rule; the description's own handler stops
  // propagation so opening one does not immediately close it.
  @HostListener('document:click')
  onDocumentClick(): void {
    this.languageMenuOpen = false;
    this.expandedProductId = '';
  }

  @HostListener('touchstart', ['$event'])
  onTouchStart(event: TouchEvent): void {
    this.edgeSwipe.active = false;
    if (event.touches.length !== 1 || this.activeView === 'teaser') {
      return;
    }
    const touch = event.touches[0];
    if (touch.clientX <= this.edgeSwipeZone) {
      this.edgeSwipe = { active: true, startX: touch.clientX, startY: touch.clientY };
    }
  }

  @HostListener('touchmove', ['$event'])
  onTouchMove(event: TouchEvent): void {
    if (!this.edgeSwipe.active || event.touches.length !== 1) {
      return;
    }
    const touch = event.touches[0];
    const dx = touch.clientX - this.edgeSwipe.startX;
    const dy = touch.clientY - this.edgeSwipe.startY;
    if (dx < 0 || Math.abs(dy) > this.edgeSwipeMaxY) {
      this.edgeSwipe.active = false;
      return;
    }
    if (dx > 10 && dx > Math.abs(dy)) {
      event.preventDefault();
    }
  }

  @HostListener('touchcancel')
  onTouchCancel(): void {
    this.edgeSwipe.active = false;
  }

  @HostListener('touchend', ['$event'])
  onTouchEnd(event: TouchEvent): void {
    if (!this.edgeSwipe.active) {
      return;
    }
    this.edgeSwipe.active = false;
    const touch = event.changedTouches[0];
    if (!touch) {
      return;
    }
    const dx = touch.clientX - this.edgeSwipe.startX;
    const dy = touch.clientY - this.edgeSwipe.startY;
    if (dx >= this.edgeSwipeMinX && Math.abs(dy) <= this.edgeSwipeMaxY) {
      this.swipeBack();
    }
  }

  ui(key: string): string {
    return MINI_MENU_COPY[this.selectedLanguage]?.[key] || MINI_MENU_COPY.en[key] || key;
  }

  languageShortLabel(language: MiniMenuPublicLanguage): string {
    const option = this.languageOptions.find((candidate) => candidate.code === language);
    return option?.shortLabel || (language === 'zh-CN' ? 'ZH.CN' : 'EN');
  }

  toggleLanguageMenu(event: Event): void {
    event.stopPropagation();
    this.languageMenuOpen = !this.languageMenuOpen;
  }

  selectLanguage(language: MiniMenuPublicLanguage, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.selectedLanguage = language;
    this.languageMenuOpen = false;
    try {
      window.localStorage.setItem(this.languageStorageKey, language);
    } catch {
      // Language is a visitor preference. Storage failures should not block the menu.
    }
  }

  go(view: MiniMenuView): void {
    if (view !== 'promos') {
      // Leaving What's On for any other destination tears the sheet down at once.
      this.resetWhatsOn();
      this.selectedPromotion = null;
    }
    this.activeView = view;
    this.expandedProductId = '';
    this.collapseSubline();
    if (view === 'teaser') {
      this.scheduleLayoutUpdate();
    }
  }

  // ---- teaser subline (More / Less, self-collapsing) ----

  toggleSubline(event: Event): void {
    event.stopPropagation();
    if (this.sublineExpanded) {
      this.collapseSubline();
      return;
    }
    this.sublineExpanded = true;
    this.armSublineTimer();
  }

  collapseSubline(): void {
    window.clearTimeout(this.sublineTimer);
    this.sublineExpanded = false;
  }

  private armSublineTimer(): void {
    window.clearTimeout(this.sublineTimer);
    this.sublineTimer = window.setTimeout(() => {
      this.sublineExpanded = false;
      this.scheduleLayoutUpdate();
    }, this.sublineIdleMs);
  }

  // ---- product description (tap to expand, no timer) ----

  isDescriptionExpanded(product: MiniMenuPublicProduct): boolean {
    return this.expandedProductId === product.id;
  }

  toggleDescription(product: MiniMenuPublicProduct, event: Event): void {
    // Without this the document listener below would collapse it in the same click.
    event.stopPropagation();
    this.expandedProductId = this.expandedProductId === product.id ? '' : product.id;
  }

  openWhatsOn(): void {
    window.clearTimeout(this.whatsOnExitTimer);
    this.whatsOnClosing = false;
    this.selectedPromotion = null;
    this.activeView = 'promos';
    this.whatsOnOpen = true;
    this.armWhatsOnTimer();
  }

  openPromotion(promotion: MiniMenuPublicPromotion): void {
    this.selectedPromotion = promotion;
    this.activeView = 'promos';
    // Reading a detail must not auto-collapse the sheet underneath it.
    window.clearTimeout(this.whatsOnTimer);
  }

  closePromotion(): void {
    this.selectedPromotion = null;
    this.armWhatsOnTimer();
  }

  // Re-starts the idle countdown; fired on open, on returning to the list and on every
  // scroll, so a list being actively read never collapses underneath the reader.
  whatsOnActivity(): void {
    if (this.whatsOnOpen && !this.whatsOnClosing && !this.selectedPromotion) {
      this.armWhatsOnTimer();
    }
  }

  // Reverse of the open: slide the sheet down, then drop back to the teaser once the
  // exit animation has played. Used by the idle timer and the back control.
  collapseWhatsOn(): void {
    if (!this.whatsOnOpen || this.whatsOnClosing) {
      this.go('teaser');
      return;
    }
    window.clearTimeout(this.whatsOnTimer);
    this.whatsOnClosing = true;
    this.whatsOnExitTimer = window.setTimeout(() => {
      this.whatsOnOpen = false;
      this.whatsOnClosing = false;
      this.selectedPromotion = null;
      this.activeView = 'teaser';
      this.scheduleLayoutUpdate();
    }, this.whatsOnExitMs);
  }

  teaserText(field: 'location' | 'eyebrow' | 'subline'): string {
    return this.translatedText(this.activeTranslation()?.teaser?.[field], this.content[field]);
  }

  /**
   * A business description conventionally opens with the business's own name
   * ("HUNGERY Cafe – A modern and inviting cafe..."). Split that leading name off so it
   * can be emphasised as the restaurant, leaving the rest of the sentence intact.
   *
   * Returns an empty lead when the description does not start with the name — nothing is
   * invented, and a business that writes its own MiniMenu subline is left alone.
   */
  get sublineLead(): string {
    return this.splitSubline().lead;
  }

  get sublineRest(): string {
    return this.splitSubline().rest;
  }

  private splitSubline(): { lead: string; rest: string } {
    const subline = this.teaserText('subline').trimStart();
    const name = String(this.content.businessName || '').trim();
    if (!name || !subline.toLowerCase().startsWith(name.toLowerCase())) {
      return { lead: '', rest: subline };
    }
    // Slice by the name's length rather than substituting it, so the description keeps
    // whatever casing and separator the business actually wrote.
    return { lead: subline.slice(0, name.length), rest: subline.slice(name.length) };
  }

  categoryName(category: MiniMenuPublicCategory): string {
    return this.translatedText(
      this.activeTranslation()?.categories?.[category.id]?.name,
      category.name
    );
  }

  productText(product: MiniMenuPublicProduct, field: 'name' | 'description'): string {
    const translation = this.activeTranslation()
      ?.categories?.[product.categoryId]
      ?.products?.[product.id]?.[field];
    return this.translatedText(translation, product[field]);
  }

  promotionText(
    promotion: MiniMenuPublicPromotion,
    field: 'badge' | 'title' | 'when' | 'lead'
  ): string {
    return this.translatedText(
      this.activeTranslation()?.promotions?.[promotion.id]?.[field],
      promotion[field]
    );
  }

  promotionBody(promotion: MiniMenuPublicPromotion): string[] {
    const translated = this.activeTranslation()?.promotions?.[promotion.id]?.body || [];
    const paragraphs = translated.map((paragraph) => String(paragraph || '').trim()).filter(Boolean);
    return paragraphs.length ? paragraphs : promotion.body;
  }

  promotionIndex(index: number): string {
    return String(index + 1).padStart(2, '0');
  }

  productImage(product: MiniMenuPublicProduct): string | null {
    return product.images?.[0] || null;
  }

  hasModifiers(product: MiniMenuPublicProduct): boolean {
    return hasMiniMenuModifiers(product.modifiers);
  }

  /** Loose modifiers — attached with no group, so they print bare with no heading. */
  looseModifiers(product: MiniMenuPublicProduct): MiniMenuPublicModifierOption[] {
    return product.modifiers?.single || [];
  }

  /**
   * Every titled group the product carries, in reading order: its own groups first,
   * then the groups contributed by each set. A set is only an authoring bundle, so its
   * groups appear here as ordinary groups under their own titles.
   */
  modifierGroups(product: MiniMenuPublicProduct): MiniMenuPublicModifierGroup[] {
    const modifiers = product.modifiers;
    if (!modifiers) {
      return [];
    }
    return [
      ...modifiers.groups,
      ...modifiers.sets.reduce(
        (groups, set) => groups.concat(set.groups),
        [] as MiniMenuPublicModifierGroup[]
      )
    ];
  }

  modifierGroupName(group: MiniMenuPublicModifierGroup): string {
    return group.name || this.ui('modifiers');
  }

  // One shared formatter: this runs from the template for every product on every
  // change detection pass, and constructing Intl.NumberFormat is the expensive part.
  private static readonly PRICE_FORMAT = new Intl.NumberFormat('id-ID', { maximumFractionDigits: 2 });

  formatPrice(value: number): string {
    const amount = MiniMenuSiteComponent.PRICE_FORMAT.format(value);
    const currency = String(this.content.currency || 'Rp').trim();
    return currency.toLowerCase() === 'rp' ? `Rp ${amount}` : `${currency}${amount}`;
  }

  formatModifierPrice(value: number): string {
    if (!value) {
      return '';
    }
    const sign = value > 0 ? '+' : '-';
    return ` ${sign}${this.formatPrice(Math.abs(value))}`;
  }

  /**
   * Evolution ships three crops per image. The tablet panes are portrait in BOTH device
   * orientations but at very different ratios, so each picks its own crop and falls back
   * gracefully when that crop has not been generated yet.
   */
  pickCrop(image: MiniMenuPublicImageSet | null): string {
    if (!image) {
      return '';
    }
    if (this.isTabletLayout) {
      return (this.isTabletPortrait
        ? image.tabletPortrait || image.tablet || image.mobile
        : image.tablet || image.mobile) || '';
    }
    return image.mobile || image.tablet || '';
  }

  scrollToCategory(categoryId: string): void {
    this.activeCategoryId = categoryId;
    // The smooth scroll travels through every category in between, and the scroll spy
    // would light each one up on the way. Hold the spy off until the jump lands.
    this.categoryJumpInFlight = true;
    window.clearTimeout(this.categoryJumpTimer);
    this.categoryJumpTimer = window.setTimeout(() => {
      this.categoryJumpInFlight = false;
    }, this.categoryJumpMs);

    this.document.getElementById(`mini-menu-${categoryId}`)
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  // Reveals the slim pill scrollbar while the product list is moving, then fades it back
  // out ~1s after scrolling stops (CSS handles the pill and the fade). Also keeps the
  // category ribbon in step with whatever is on screen.
  onMenuScroll(event?: Event): void {
    if (!this.menuScrolling) {
      this.menuScrolling = true;
    }
    window.clearTimeout(this.menuScrollTimer);
    this.menuScrollTimer = window.setTimeout(() => { this.menuScrolling = false; }, 1000);

    const scroller = event?.target as HTMLElement | null;
    if (scroller && !this.categoryJumpInFlight) {
      this.syncActiveCategory(scroller);
    }
  }

  /**
   * Marks the category whose heading has most recently passed the top of the list as the
   * current one, so the ribbon tracks the scroll rather than only the last tap.
   */
  private syncActiveCategory(scroller: HTMLElement): void {
    if (!this.menuCategories.length) {
      return;
    }

    const line = scroller.getBoundingClientRect().top + this.categorySpyOffset;
    let current = this.menuCategories[0].id;
    this.menuCategories.forEach((category) => {
      const heading = this.document.getElementById(`mini-menu-${category.id}`);
      if (heading && heading.getBoundingClientRect().top <= line + 1) {
        current = category.id;
      }
    });

    // A short final category may never reach the line, so the end of the list always
    // belongs to it — otherwise the ribbon sticks on the second-to-last category.
    if (scroller.scrollTop + scroller.clientHeight >= scroller.scrollHeight - 2) {
      current = this.menuCategories[this.menuCategories.length - 1].id;
    }

    if (current !== this.activeCategoryId) {
      this.activeCategoryId = current;
      this.revealActivePill(current);
    }
  }

  /** Keeps the newly active pill in view when the ribbon itself is scrollable. */
  private revealActivePill(categoryId: string): void {
    const ribbon = this.document.querySelector<HTMLElement>('#cats');
    const pill = this.document.getElementById(`mini-menu-cat-${categoryId}`);
    if (!ribbon || !pill || ribbon.scrollWidth <= ribbon.clientWidth + 1) {
      return;
    }
    pill.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
  }

  trackByCategory(_index: number, category: MiniMenuPublicCategory): string {
    return category.id;
  }

  trackByProduct(_index: number, product: MiniMenuPublicProduct): string {
    return product.id;
  }

  trackByModifierGroup(_index: number, group: MiniMenuPublicModifierGroup): string {
    return group.id;
  }

  trackByModifierOption(_index: number, option: MiniMenuPublicModifierOption): string {
    return option.id;
  }

  trackByPromotion(_index: number, promotion: MiniMenuPublicPromotion): string {
    return promotion.id;
  }

  private swipeBack(): void {
    if (this.selectedPromotion) {
      this.closePromotion();
      return;
    }
    if (this.activeView === 'promos') {
      if (this.whatsOnOpen && !this.whatsOnClosing) {
        this.collapseWhatsOn();
      } else {
        this.go('teaser');
      }
      return;
    }
    if (this.activeView === 'menu') {
      this.go('teaser');
    }
  }

  private armWhatsOnTimer(): void {
    window.clearTimeout(this.whatsOnTimer);
    this.whatsOnTimer = window.setTimeout(() => this.collapseWhatsOn(), this.whatsOnIdleMs);
  }

  private resetWhatsOn(): void {
    window.clearTimeout(this.whatsOnTimer);
    window.clearTimeout(this.whatsOnExitTimer);
    this.whatsOnOpen = false;
    this.whatsOnClosing = false;
  }

  private load(slug: string): void {
    this.content = {
      ...LOADING_MINI_MENU_PUBLIC_CONTENT,
      slug,
      theme: { ...LOADING_MINI_MENU_PUBLIC_CONTENT.theme }
    };
    this.activeView = 'teaser';
    this.activeCategoryId = '';
    this.selectedPromotion = null;
    this.expandedProductId = '';
    this.sublineOverflows = false;
    this.collapseSubline();
    this.recomputeViews();

    this.subscriptions.add(
      this.miniMenuService.getPublicMenu(slug).subscribe({
        next: (content) => {
          this.content = content;
          this.selectedLanguage = content.languages.some((language) =>
            language.code === this.selectedLanguage)
            ? this.selectedLanguage
            : content.defaultLanguage;
          this.recomputeViews();
          this.scheduleLayoutUpdate();
        },
        error: (error) => {
          this.content = {
            ...this.content,
            loadState: error?.status === 404 ? 'unavailable' : 'error'
          };
          this.recomputeViews();
        }
      })
    );
  }

  private recomputeViews(): void {
    this.livePromotions = [...this.content.promotions]
      .sort((left, right) => left.sortOrder - right.sortOrder);
    this.menuCategories = this.content.categories
      .filter((category) => category.products.length > 0);
    this.activeCategoryId = this.menuCategories[0]?.id || '';
  }

  private logoMask(customUrl: string | null, fallback: string): string {
    const url = customUrl || fallback;
    return `url("${url.replace(/"/g, '%22')}")`;
  }

  private activeTranslation() {
    return this.selectedLanguage === 'en'
      ? null
      : this.content.translations[this.selectedLanguage] || null;
  }

  private translatedText(value: string | undefined, fallback: string): string {
    const text = String(value || '').trim();
    return text || fallback;
  }

  private readStoredLanguage(): MiniMenuPublicLanguage {
    try {
      const stored = String(window.localStorage.getItem(this.languageStorageKey) || '').toLowerCase();
      return stored === 'zh-cn' || stored === 'zh-tw' ? 'zh-CN' : 'en';
    } catch {
      return 'en';
    }
  }

  private updateLayoutMode(): void {
    const shell = this.appShell?.nativeElement;
    const shellWidth = shell?.getBoundingClientRect().width || window.innerWidth;
    // The shell widens to a tablet width via CSS at >=768px; allow for a scrollbar so
    // the boundary does not flip-flop right at 768.
    this.isTabletLayout = shellWidth >= 720;
    this.isTabletPortrait = this.isTabletLayout && window.innerHeight > window.innerWidth;
    this.measureSubline();

    if (!shell) {
      return;
    }

    const teaser = shell.querySelector<HTMLElement>('#teaser');
    const topBand = teaser?.querySelector<HTMLElement>('.t-head');
    const actionBand = teaser?.querySelector<HTMLElement>('.t-actions');
    const teaserRect = teaser?.getBoundingClientRect();
    const topRect = topBand?.getBoundingClientRect();
    const actionRect = actionBand?.getBoundingClientRect();
    // Skip while the teaser is hidden (another view active) so the last good frame is
    // kept instead of one computed from a zero-size element.
    if (teaserRect && teaserRect.height < 1) {
      return;
    }
    // The teaser is the whole shell on mobile and the LEFT pane on tablet. That pane is
    // 0.84fr of the shell (see .app.tablet-layout grid-template-columns) — derive its
    // width from the shell so the frame is right even before the grid resizes the teaser.
    const paneWidth = this.isTabletLayout ? shellWidth * (0.84 / 1.84) : (teaserRect?.width || shellWidth);
    const teaserHeight = teaserRect?.height || window.innerHeight || 760;
    const topBoundary = teaserRect && topRect ? topRect.bottom - teaserRect.top : 84;
    const actionBoundary = teaserRect && actionRect ? actionRect.top - teaserRect.top : teaserHeight - 118;
    const sideMargin = 20;
    const topGap = 12;
    const bottomGap = 10;
    const frameTop = topBoundary + topGap;
    const frameHeight = Math.max(160, actionBoundary - bottomGap - frameTop);
    const frameWidth = Math.max(0, paneWidth - sideMargin * 2);

    shell.style.setProperty('--teaser-frame-top', `${Math.round(frameTop)}px`);
    shell.style.setProperty('--teaser-frame-left', `${Math.round(sideMargin)}px`);
    shell.style.setProperty('--teaser-frame-width', `${Math.round(frameWidth)}px`);
    shell.style.setProperty('--teaser-frame-height', `${Math.round(frameHeight)}px`);
  }

  /**
   * Adds the MiniMenu webfont stylesheet once per document. Without this the design falls
   * back to system fonts everywhere — see MINI_MENU_FONT_HREF for why it cannot live in
   * the component's SCSS.
   */
  private ensureFonts(): void {
    if (this.document.getElementById(MINI_MENU_FONT_LINK_ID)) {
      return;
    }
    const link = this.document.createElement('link');
    link.id = MINI_MENU_FONT_LINK_ID;
    link.rel = 'stylesheet';
    link.href = MINI_MENU_FONT_HREF;
    this.document.head.appendChild(link);

    // Text metrics change once the real faces swap in, so re-measure the subline clamp
    // rather than deciding on fallback-font line counts.
    this.document.fonts?.ready?.then(() => this.scheduleLayoutUpdate());
  }

  /**
   * The More control only earns its place when the subline is actually longer than its
   * three-line clamp. Measured rather than guessed, because the copy comes from whatever
   * the business set (or, unset, from its full description).
   */
  private measureSubline(): void {
    const element = this.sublineText?.nativeElement;
    // Preserve the last overflow decision while either direction of the height animation
    // is running. Measuring an unclamped closing frame reports no overflow and would
    // remove the More/Less control after the first cycle.
    if (!element || this.sublineExpanded || element.style.maxHeight) {
      return;
    }
    // While the teaser is hidden the element reports zero height; keep the last good
    // reading rather than deciding there is nothing to expand.
    if (element.clientHeight < 1) {
      return;
    }
    this.sublineOverflows = element.scrollHeight - element.clientHeight > 1;
  }

  private scheduleLayoutUpdate(): void {
    if (typeof window === 'undefined') {
      return;
    }
    if (this.layoutRaf) {
      window.cancelAnimationFrame(this.layoutRaf);
    }
    this.layoutRaf = window.requestAnimationFrame(() => this.updateLayoutMode());
  }
}
